import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Loader2, ListOrdered, Coins, Cpu, Package } from "lucide-react";
import { AdminShell } from "@/components/AdminShell";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { adminQueue, adminJobDetail } from "@/lib/job-queue.functions";
import { JOB_KIND_LABEL, type JobKind } from "@/lib/job-queue";
import { formatSek } from "@/lib/ai-costs";

const time = (v: string | null) =>
  v ? new Date(v).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" }) : "—";

const dur = (ms: number | null) => (ms ? `${(ms / 1000).toFixed(1)} s` : "—");

const sek = (v: number | null | undefined) => (v == null ? "—" : `${formatSek(v, 3)} kr`);

function planTone(plan: string) {
  if (plan === "expert") return "bg-primary/15 text-primary";
  if (plan === "pro") return "bg-emerald-500/15 text-emerald-600";
  if (plan === "plus") return "bg-amber-500/15 text-amber-600";
  return "bg-muted text-muted-foreground";
}

/** Detaljvy: exakt vad körningen gjorde, vad den hittade och vad den kostade. */
function JobDetailDialog({ id, onClose }: { id: string | null; onClose: () => void }) {
  const load = useServerFn(adminJobDetail);
  const q = useQuery({
    queryKey: ["admin-job-detail", id],
    queryFn: () => load({ data: { id: id! } }),
    enabled: !!id,
  });
  const d = q.data;

  return (
    <Dialog open={!!id} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {d ? (JOB_KIND_LABEL[d.kind as JobKind] ?? d.kind) : "Körning"}
          </DialogTitle>
          <DialogDescription className="break-words">{d?.label || "Detaljer om körningen"}</DialogDescription>
        </DialogHeader>

        {q.isLoading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Hämtar körningen…
          </div>
        ) : q.error ? (
          <p className="text-sm text-destructive">{(q.error as Error).message}</p>
        ) : d ? (
          <div className="space-y-5 text-sm">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs uppercase text-muted-foreground">Användare</p>
                <Link
                  to="/admin/anvandare/$userId"
                  params={{ userId: d.userId }}
                  className="font-medium text-primary hover:underline"
                >
                  {d.userName || d.userId.slice(0, 8)}
                </Link>
                {d.userEmail && (
                  <p className="break-words text-xs text-muted-foreground">{d.userEmail}</p>
                )}
                <span className={`mt-1 inline-block rounded px-2 py-0.5 text-xs ${planTone(d.plan)}`}>
                  {d.plan}
                </span>
              </div>
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs uppercase text-muted-foreground">Kostnad</p>
                <p className="text-lg font-semibold">{sek(d.costSek)}</p>
                <p className="text-xs text-muted-foreground">
                  {d.credits == null ? "Ingen förbrukning registrerad" : `${d.credits.toFixed(4)} krediter`}
                </p>
              </div>
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs uppercase text-muted-foreground">Resultat</p>
                <p className="font-medium">
                  {d.itemCount == null ? "—" : `${d.itemCount} prylar`}
                </p>
                {d.valueLow != null && d.valueHigh != null && (
                  <p className="text-xs text-muted-foreground">
                    Uppskattat värde {d.valueLow.toLocaleString("sv-SE")}–
                    {d.valueHigh.toLocaleString("sv-SE")} kr
                  </p>
                )}
              </div>
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs uppercase text-muted-foreground">Tider</p>
                <p className="text-xs">Köad: {time(d.queuedAt)}</p>
                <p className="text-xs">Startad: {time(d.startedAt)}</p>
                <p className="text-xs">Klar: {time(d.finishedAt)}</p>
                <p className="text-xs">
                  Total tid: {dur(d.durationMs)} • försök: {d.attempts}
                </p>
              </div>
            </div>

            {d.error && (
              <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-xs whitespace-pre-wrap text-destructive">
                {d.error}
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Cpu className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold">Vad som kördes</h3>
              </div>
              {d.callSource === "none" ? (
                <p className="text-xs text-muted-foreground">
                  Ingen anropslogg sparad för den här körningen – totalen ovan kommer från jobbets
                  registrerade förbrukning.
                </p>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground">
                    Källa:{" "}
                    {d.callSource === "direct"
                      ? "direkt kopplad till den här körningen"
                      : d.callSource === "job"
                        ? "körningens egen äldre anropslogg"
                        : "äldre uppskattning via användare och tidsfönster"}
                    .
                  </p>
                  <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="w-full min-w-[520px] text-xs">
                      <thead className="bg-muted/50 text-left uppercase text-muted-foreground">
                        <tr>
                          <th className="px-2 py-1.5">Steg</th>
                          <th className="px-2 py-1.5">Modell</th>
                          <th className="px-2 py-1.5">Tokens</th>
                          <th className="px-2 py-1.5">Tid</th>
                          <th className="px-2 py-1.5">Kostnad</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.calls.map((c, i) => (
                          <tr key={i} className="border-t border-border">
                            <td className="px-2 py-1.5">
                              {c.label}
                              {!c.ok && <span className="ml-1 text-destructive">(fel)</span>}
                            </td>
                            <td className="px-2 py-1.5 break-words">{c.model}</td>
                            <td className="px-2 py-1.5">
                              {c.inputTokens ?? "—"} / {c.outputTokens ?? "—"}
                            </td>
                            <td className="px-2 py-1.5">{dur(c.durationMs)}</td>
                            <td className="px-2 py-1.5">{sek(c.sek)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t border-border bg-muted/30 font-semibold">
                          <td className="px-2 py-1.5" colSpan={4}>
                            Summa av raderna
                          </td>
                          <td className="px-2 py-1.5">
                            {sek(d.calls.reduce((a, c) => a + (c.sek ?? 0), 0))}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/** Adminvy: turordning, nivåfördelning och historik för bakgrundskön. */
function QueuePage() {
  const load = useServerFn(adminQueue);
  const [openId, setOpenId] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ["admin-queue"],
    queryFn: () => load(),
    refetchInterval: 5000,
  });

  return (
    <AdminShell title="Kö">
      {q.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Hämtar kön…
        </div>
      ) : q.error ? (
        <p className="text-sm text-destructive">{(q.error as Error).message}</p>
      ) : (
        <div className="space-y-8">
          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <ListOrdered className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-sm font-semibold">
                I kön just nu ({q.data?.waiting.length ?? 0})
              </h2>
            </div>
            <p className="text-xs text-muted-foreground">
              Fördelning när det är tryck: Expert 60 %, Pro 30 %, Plus 10 %. Lägre prioritetssiffra
              plockas först.
            </p>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Plats</th>
                    <th className="px-3 py-2">Användare</th>
                    <th className="px-3 py-2">Nivå</th>
                    <th className="px-3 py-2">Typ</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Kostnad hittills</th>
                    <th className="px-3 py-2">Prio</th>
                    <th className="px-3 py-2">Köad</th>
                    <th className="px-3 py-2">Startad</th>
                  </tr>
                </thead>
                <tbody>
                  {(q.data?.waiting ?? []).map((j) => (
                    <tr key={j.id} className="border-t border-border">
                      <td className="px-3 py-2 font-medium">
                        {j.status === "running" ? "Kör" : j.place}
                      </td>
                      <td className="px-3 py-2 break-words">
                        <Link
                          to="/admin/anvandare/$userId"
                          params={{ userId: j.userId }}
                          className="text-primary hover:underline"
                        >
                          {j.user}
                        </Link>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`rounded px-2 py-0.5 text-xs ${planTone(j.plan)}`}>
                          {j.plan}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        {JOB_KIND_LABEL[j.kind as JobKind] ?? j.kind}
                        <span className="block text-xs text-muted-foreground break-words">
                          {j.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 min-w-[190px]">
                        <Badge variant={j.status === "running" ? "default" : "secondary"}>
                          {j.status === "running" ? "Kör" : "Väntar"}
                        </Badge>
                        {j.status === "running" && (
                          <div className="mt-1.5 space-y-1">
                            <p className="text-xs text-foreground break-words">
                              {j.progress
                                ? `${j.progress.step} (${j.progress.index}/${j.progress.total})`
                                : "Startar…"}
                            </p>
                            <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
                              <div
                                className="h-full bg-primary transition-all duration-500"
                                style={{ width: `${j.progress?.percent ?? 4}%` }}
                              />
                            </div>
                            <p className="text-[11px] text-muted-foreground">
                              Senaste signal: {j.progressAt ? time(j.progressAt) : "–"}
                            </p>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span>{sek(j.costSek)}</span>
                        <span className="block text-[11px] text-muted-foreground">
                          {j.costSource === "direct"
                            ? "Direkt loggad"
                            : j.costSource === "checkpoint"
                              ? "Checkpoint"
                              : "Inget anrop än"}
                        </span>
                      </td>
                      <td className="px-3 py-2">{j.priority}</td>
                      <td className="px-3 py-2">{time(j.queuedAt)}</td>
                      <td className="px-3 py-2">{time(j.startedAt)}</td>
                    </tr>
                  ))}
                  {!q.data?.waiting.length && (
                    <tr>
                      <td className="px-3 py-6 text-center text-muted-foreground" colSpan={9}>
                        Kön är tom.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold">Senast körda</h2>
              <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                <span>{q.data?.totals?.runs ?? 0} körningar</span>
                <span className="inline-flex items-center gap-1">
                  <Package className="h-3 w-3" />
                  {q.data?.totals?.items ?? 0} prylar
                </span>
                <span className="inline-flex items-center gap-1">
                  <Coins className="h-3 w-3" />
                  {sek(q.data?.totals?.costSek ?? 0)}
                </span>
                <span>{dur(q.data?.totals?.durationMs ?? 0)} total tid</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Klicka på en rad för att se exakt vilka AI-steg som kördes och vad de kostade.
            </p>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full min-w-[820px] text-sm">
                <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Användare</th>
                    <th className="px-3 py-2">Nivå</th>
                    <th className="px-3 py-2">Typ</th>
                    <th className="px-3 py-2">Prylar</th>
                    <th className="px-3 py-2">Kostnad</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Tid</th>
                    <th className="px-3 py-2">Klar</th>
                  </tr>
                </thead>
                <tbody>
                  {(q.data?.recent ?? []).map((j) => (
                    <tr
                      key={j.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setOpenId(j.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setOpenId(j.id);
                        }
                      }}
                      className="cursor-pointer border-t border-border transition-colors hover:bg-muted/40"
                    >
                      <td className="px-3 py-2 break-words">
                        <Link
                          to="/admin/anvandare/$userId"
                          params={{ userId: j.userId }}
                          onClick={(e) => e.stopPropagation()}
                          className="text-primary hover:underline"
                        >
                          {j.user}
                        </Link>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`rounded px-2 py-0.5 text-xs ${planTone(j.plan)}`}>
                          {j.plan}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        {JOB_KIND_LABEL[j.kind as JobKind] ?? j.kind}
                        <span className="block text-xs text-muted-foreground break-words">
                          {j.label}
                        </span>
                      </td>
                      <td className="px-3 py-2">{j.itemCount ?? "—"}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{sek(j.costSek)}</td>
                      <td className="px-3 py-2">
                        <Badge variant={j.status === "done" ? "secondary" : "destructive"}>
                          {j.status === "done" ? "Klar" : j.status === "failed" ? "Fel" : "Avbruten"}
                        </Badge>
                        {j.error && (
                          <span className="block max-w-xs text-xs text-destructive break-words whitespace-pre-wrap">
                            {j.error}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">{dur(j.durationMs)}</td>
                      <td className="px-3 py-2">{time(j.finishedAt)}</td>
                    </tr>
                  ))}
                  {!q.data?.recent.length && (
                    <tr>
                      <td className="px-3 py-6 text-center text-muted-foreground" colSpan={8}>
                        Inga körningar än.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <JobDetailDialog id={openId} onClose={() => setOpenId(null)} />
        </div>
      )}
    </AdminShell>
  );
}

export const Route = createFileRoute("/_authenticated/admin/kon")({
  head: () => ({
    meta: [
      { title: "Kö — Administration" },
      {
        name: "description",
        content: "Internt: turordning, nivåfördelning och historik för bakgrundskörningar.",
      },
      { name: "robots", content: "noindex" },
      { property: "og:title", content: "Kö — Administration" },
      { property: "og:description", content: "Turordning och historik för bakgrundskörningar." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: QueuePage,
});
